import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { churchesApi } from '@app/api/organization';
import { useAuth } from '@app/auth/AuthContext';
import type { Church } from '@app/types';

interface ChurchContextType {
  church: Church | null;
  isPortedToChurch: boolean;
  isLoading: boolean;
  refetchChurch: () => Promise<void>;
}

const ChurchContext = createContext<ChurchContextType | undefined>(undefined);

export const ChurchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [church, setChurch] = useState<Church | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchChurch = useCallback(async () => {
    if (!user?.churchId) {
      setChurch(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const data = await churchesApi.getById(user.churchId);
      setChurch(data);
    } catch (err) {
      console.error('Failed to fetch church:', err);
      setChurch(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.churchId]);

  useEffect(() => {
    fetchChurch();
  }, [fetchChurch]);

  const isPortedToChurch = church?.isPortedToChurch ?? false;

  return (
    <ChurchContext.Provider
      value={{
        church,
        isPortedToChurch,
        isLoading,
        refetchChurch: fetchChurch,
      }}
    >
      {children}
    </ChurchContext.Provider>
  );
};

export const useChurch = (): ChurchContextType => {
  const context = useContext(ChurchContext);
  if (!context) {
    throw new Error('useChurch must be used within a ChurchProvider');
  }
  return context;
};
